import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import {
  FreelancerPublic,
  UpdateFreelancerRequest,
  updateFreelancerByIdApi,
} from "../../freelancer/freelancerApi";
import { maskCpfInput } from "../../dp/utils/format";

import "../pages/AdminSistema.css";

type Props = {
  isOpen: boolean;
  /** Freelancer já escolhido na tabela — este modal não busca ninguém. */
  freelancer: FreelancerPublic | null;
  onClose: () => void;
  onUpdated?: () => void;
};

const onlyDigits = (value: string) => value.replace(/\D/g, "");

/**
 * Decide se um campo entra no PATCH.
 *
 * Mesma regra do modal administrativo antigo: campo inalterado não é enviado, e
 * campo esvaziado vira `""` (o backend entende como "limpar"). Mandar tudo
 * sempre apagaria dados que o operador não tocou.
 */
const patchField = (nextRaw: string, prevValue?: string | null) => {
  const prev = (prevValue ?? "").trim();
  const next = nextRaw.trim();

  if (next === prev) return undefined;
  if (next === "" && prev !== "") return "";
  if (next !== "" && next !== prev) return next;
  return undefined;
};

const FreelancerEditModal: React.FC<Props> = ({
  isOpen,
  freelancer,
  onClose,
  onUpdated,
}) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [contato, setContato] = useState("");
  const [pix, setPix] = useState("");
  const [banco, setBanco] = useState("");
  const [rua, setRua] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !freelancer) return;

    setNomeCompleto(freelancer.nomeCompleto ?? "");
    setCpf(maskCpfInput(freelancer.cpf ?? ""));
    setContato(freelancer.contato ?? "");
    setPix(freelancer.pix ?? "");
    setBanco(freelancer.banco ?? "");
    setRua(freelancer.rua ?? "");
    setNumero(freelancer.numero ?? "");
    setComplemento(freelancer.complemento ?? "");
    setBairro(freelancer.bairro ?? "");
    setCidade(freelancer.cidade ?? "");
    setEstado(freelancer.estado ?? "");
  }, [isOpen, freelancer]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = anterior;
    };
  }, [isOpen, onClose, saving]);

  const errosDoFormulario = useMemo(() => {
    const erros: string[] = [];
    if (!nomeCompleto.trim()) erros.push("Informe o nome completo.");
    if (onlyDigits(cpf).length !== 11) erros.push("CPF inválido (deve ter 11 dígitos).");
    if (estado.trim() && estado.trim().length !== 2) erros.push("UF deve ter 2 letras.");
    return erros;
  }, [nomeCompleto, cpf, estado]);

  const handleSave: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!freelancer || !token) return;
    if (errosDoFormulario.length > 0) {
      toast.error(errosDoFormulario[0]);
      return;
    }

    const dto: UpdateFreelancerRequest = {};

    const nomePatch = patchField(nomeCompleto, freelancer.nomeCompleto);
    if (nomePatch !== undefined) dto.nomeCompleto = nomePatch;

    const cpfDigits = onlyDigits(cpf);
    if (cpfDigits !== (freelancer.cpf ?? "").trim()) dto.cpf = cpfDigits;

    const campos: Array<[keyof UpdateFreelancerRequest, string, string | null | undefined]> = [
      ["contato", contato, freelancer.contato],
      ["pix", pix, freelancer.pix],
      ["banco", banco, freelancer.banco],
      ["rua", rua, freelancer.rua],
      ["numero", numero, freelancer.numero],
      ["complemento", complemento, freelancer.complemento],
      ["bairro", bairro, freelancer.bairro],
      ["cidade", cidade, freelancer.cidade],
    ];

    campos.forEach(([chave, valor, anterior]) => {
      const patch = patchField(valor, anterior);
      if (patch !== undefined) (dto as Record<string, unknown>)[chave] = patch;
    });

    const estadoPatch = patchField(estado.toUpperCase(), freelancer.estado);
    if (estadoPatch !== undefined) dto.estado = estadoPatch;

    if (Object.keys(dto).length === 0) {
      toast.info("Nenhuma alteração para salvar.");
      onClose();
      return;
    }

    try {
      setSaving(true);
      await updateFreelancerByIdApi(token, freelancer.id, dto);
      toast.success("Freelancer atualizado com sucesso.");
      onUpdated?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao atualizar freelancer.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !freelancer) return null;

  return (
    <div
      className="admModalOverlay"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="admModal"
        role="dialog"
        aria-modal="true"
        aria-label="Editar freelancer"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSave}>
          <div className="admModal__header">
            <h2 className="admModal__title">Editar freelancer</h2>
            <button
              type="button"
              className="admModal__close"
              onClick={onClose}
              disabled={saving}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>

          <div className="admModal__body">
            <div className="admFormGroup">
              <label htmlFor="admFreeNome">Nome completo</label>
              <input
                id="admFreeNome"
                className="admInput"
                value={nomeCompleto}
                onChange={(e) => setNomeCompleto(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="admFormGrid">
              <div className="admFormGroup">
                <label htmlFor="admFreeCpf">CPF</label>
                <input
                  id="admFreeCpf"
                  className="admInput"
                  value={cpf}
                  onChange={(e) => setCpf(maskCpfInput(e.target.value))}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>

              <div className="admFormGroup">
                <label htmlFor="admFreeContato">Contato</label>
                <input
                  id="admFreeContato"
                  className="admInput"
                  value={contato}
                  onChange={(e) => setContato(e.target.value)}
                  inputMode="tel"
                  autoComplete="off"
                />
              </div>

              <div className="admFormGroup">
                <label htmlFor="admFreePix">Chave PIX</label>
                <input
                  id="admFreePix"
                  className="admInput"
                  value={pix}
                  onChange={(e) => setPix(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="admFormGroup">
                <label htmlFor="admFreeBanco">Banco</label>
                <input
                  id="admFreeBanco"
                  className="admInput"
                  value={banco}
                  onChange={(e) => setBanco(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="admSection" style={{ marginBottom: 0 }}>
              <h3 className="admSection__title">Endereço</h3>
              <div className="admFormGrid">
                <div className="admFormGroup">
                  <label htmlFor="admFreeRua">Rua</label>
                  <input
                    id="admFreeRua"
                    className="admInput"
                    value={rua}
                    onChange={(e) => setRua(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="admFormGroup">
                  <label htmlFor="admFreeNumero">Número</label>
                  <input
                    id="admFreeNumero"
                    className="admInput"
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="admFormGroup">
                  <label htmlFor="admFreeComplemento">Complemento</label>
                  <input
                    id="admFreeComplemento"
                    className="admInput"
                    value={complemento}
                    onChange={(e) => setComplemento(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="admFormGroup">
                  <label htmlFor="admFreeBairro">Bairro</label>
                  <input
                    id="admFreeBairro"
                    className="admInput"
                    value={bairro}
                    onChange={(e) => setBairro(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="admFormGroup">
                  <label htmlFor="admFreeCidade">Cidade</label>
                  <input
                    id="admFreeCidade"
                    className="admInput"
                    value={cidade}
                    onChange={(e) => setCidade(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="admFormGroup">
                  <label htmlFor="admFreeEstado">UF</label>
                  <input
                    id="admFreeEstado"
                    className="admInput"
                    value={estado}
                    onChange={(e) => setEstado(e.target.value.toUpperCase().slice(0, 2))}
                    maxLength={2}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            {errosDoFormulario.length > 0 ? (
              <ul className="admErrorList">
                {errosDoFormulario.map((erro) => (
                  <li key={erro}>{erro}</li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="admModal__footer">
            <button
              type="button"
              className="admBtn admBtn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="admBtn admBtn--primary"
              disabled={saving || errosDoFormulario.length > 0}
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FreelancerEditModal;
